import { useCallback, useEffect, useState } from "react";
import { useSubmitApi } from "./authHook";
import { ApiCalls } from "../../components/forms/providerAppointmentForms/AllAppointmentsApi";
import {
  AttestationDateRange,
  AttestationStatus,
} from "../../types/attestation";

interface UseAttestationStatusResult {
  attestationStatus: AttestationStatus | null;
  loading: boolean;
  refresh: () => void;
}

/**
 * Attestation status for the provider. Returns null until loaded, and on failure — the schedule
 * must stay usable even when this call is unavailable, so the caller simply renders nothing
 * rather than surfacing an error.
 *
 * `visibleRange` is the window the calendar is showing, and it is what makes the day strip able
 * to mark days outside the pay period: the response covers the requested range as well as the
 * live pay period, so one call feeds both the markers and the banner. Changing the range
 * refetches, which is how navigating to another week fills in that week's markers.
 */
const useAttestationStatus = (
  providerId?: number,
  visibleRange?: AttestationDateRange
): UseAttestationStatusResult => {
  const { submitApiCall } = useSubmitApi();
  const [attestationStatus, setAttestationStatus] =
    useState<AttestationStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  /*
   * Depended on as two strings rather than as the object: the caller builds the range while
   * rendering, so a new object arrives every render and keying the fetch on it would loop.
   */
  const rangeStart = visibleRange?.startDate;
  const rangeEnd = visibleRange?.endDate;

  const fetchStatus = useCallback(async () => {
    if (!providerId) {
      return;
    }
    setLoading(true);
    try {
      const response = await submitApiCall(ApiCalls.AttestationStatusCall, {
        providerId,
        /*
         * Sent only once the calendar has reported what it is showing. Omitted rather than
         * guessed: with no range the response still covers the live pay period, which is what
         * the banner needs, and the wider markers fill in on the fetch that follows.
         */
        ...(rangeStart && rangeEnd
          ? { startDate: rangeStart, endDate: rangeEnd }
          : {}),
      });
      setAttestationStatus(response?.data ?? null);
    } catch (error) {
      console.log("[useAttestationStatus] failed to load status", error);
      setAttestationStatus(null);
    } finally {
      setLoading(false);
    }
    // submitApiCall is recreated each render by useSubmitApi, so it is intentionally
    // excluded to avoid an infinite fetch loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId, rangeStart, rangeEnd]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return { attestationStatus, loading, refresh: fetchStatus };
};

export { useAttestationStatus };
